import { DEFAULT_TOOLBAR_MOUSE_ENTER_DELAY } from "@org-avp/avp-avengers-ui-shared-utils";
import { Button, ButtonProps, Tooltip, TooltipProps } from "antd";
import { TFunction } from "i18next";
import { FC, UIEvent, useCallback, useMemo } from "react";
import { IContextAction } from "./context-action";
import { getFirstString, getShortcutText, optT } from "./util";

// toolbar button
export type ActionToolbarButtonProps = ButtonProps & {
  action: IContextAction<string>;
  t: TFunction;
  tooltipPlacement?: TooltipProps["placement"];
  mouseEnterDelay?: TooltipProps["mouseEnterDelay"];
  onClickValue?: string;
};

export const ActionToolbarButton: FC<ActionToolbarButtonProps> = (
  { action: { action, disabled, icon, shortcut, text, textKey, tooltip, tooltipKey }, t, tooltipPlacement, onClickValue, ...props }: ActionToolbarButtonProps,
) => {
  const onClick = useCallback(
    (e: UIEvent) => {
      e.stopPropagation();
      action(onClickValue);
    },
    [action, onClickValue],
  );

  const tooltipTitle = useMemo(
    () => {
      const tooltipText = getFirstString(tooltip, optT(t, tooltipKey), text, t(textKey));
      const shortcutText = shortcut ? getShortcutText(shortcut) : undefined;
      return (shortcutText ? `${tooltipText} (${shortcutText})` : tooltipText);
    },
    [shortcut, t, text, textKey, tooltip, tooltipKey],
  );
  const mouseEnterDelay = useMemo(() => {
    return props.mouseEnterDelay ?? DEFAULT_TOOLBAR_MOUSE_ENTER_DELAY;
  }, [props.mouseEnterDelay]);

  return (
    <Tooltip title={tooltipTitle} placement={tooltipPlacement} mouseEnterDelay={mouseEnterDelay}>
      <Button disabled={disabled} icon={icon} onClick={onClick} {...props}/>
    </Tooltip>
  );
};

// toolbar button with multi string values
export type ActionToolbarButtonMultiProps = Omit<ActionToolbarButtonProps, "action" | "onClickValue"> & {
  action: IContextAction<string[]>;
  onClickValues?: string[];
};

export const ActionToolbarButtonMulti: FC<ActionToolbarButtonMultiProps> = (
  {
    action: { action, disabled, icon, shortcut, text, textKey, tooltip, tooltipKey },
    t,
    tooltipPlacement,
    onClickValues,
    ...props
  }: ActionToolbarButtonMultiProps,
) => {
  const onClick = useCallback(
    (e: UIEvent) => {
      e.stopPropagation();
      action(onClickValues);
    },
    [action, onClickValues],
  );

  const tooltipTitle = useMemo(
    () => {
      const tooltipText = getFirstString(tooltip, optT(t, tooltipKey), text, t(textKey));
      const shortcutText = shortcut ? getShortcutText(shortcut) : undefined;
      return (shortcutText ? `${tooltipText} (${shortcutText})` : tooltipText);
    },
    [shortcut, t, text, textKey, tooltip, tooltipKey],
  );
  const mouseEnterDelay = useMemo(() => {
    return props.mouseEnterDelay ?? DEFAULT_TOOLBAR_MOUSE_ENTER_DELAY;
  }, [props.mouseEnterDelay]);

  return (
    <Tooltip title={tooltipTitle} placement={tooltipPlacement} mouseEnterDelay={mouseEnterDelay}>
      <Button disabled={disabled} icon={icon} onClick={onClick} {...props}/>
    </Tooltip>
  );
};
